import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Đồ thị độ dịch chuyển – thời gian (Lớp 10 — Bài 4): chuyển động thẳng đều.
// Đồ thị d–t được GHI TRỰC TIẾP theo chuyển động (không tính lại từ công thức) nên khi
// đổi vận tốc lúc đang chạy, độ dốc đồ thị thay đổi từ thời điểm đó (xuất hiện điểm gãy).
const TOPW = 660, TOPH = 110, GW = 660, GH = 250
const T = 10, VMAX = 20, DMAX = VMAX * T   // v tối đa = 20 m/s, t tối đa = 10 s → d tối đa = 200 m

const DisplacementTimeSimulation: React.FC<Props> = ({
  title = 'Đồ thị độ dịch chuyển – thời gian',
  description = 'Chuyển động thẳng đều: đồ thị d–t là đường thẳng, độ dốc = vận tốc. Đổi vận tốc khi đang chạy → độ dốc đồ thị đổi theo (điểm gãy).',
  onComplete,
}) => {
  const top = useRef<HTMLCanvasElement>(null)
  const gcv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const dRef = useRef(0)
  const hist = useRef<{ t: number; d: number }[]>([{ t: 0, d: 0 }])
  const [v, setV] = useState(4)
  const [running, setRunning] = useState(false)
  const vRef = useRef(v), runRef = useRef(running)
  const firstRef = useRef(false)
  useEffect(() => { vRef.current = v }, [v])
  useEffect(() => { runRef.current = running }, [running])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const reset = () => { setRunning(false); tRef.current = 0; dRef.current = 0; hist.current = [{ t: 0, d: 0 }] }

  const drawTop = useCallback(() => {
    const ctx = prepCanvas(top.current, TOPW, TOPH); if (!ctx) return
    ctx.clearRect(0, 0, TOPW, TOPH)
    const x0 = 40, x1 = TOPW - 40, cy = TOPH / 2 + 6, Ox = (x0 + x1) / 2, half = (x1 - x0) / 2
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(x0, cy + 18, x1 - x0, 5) // ray
    // gốc O ở giữa (cho phép d âm/dương)
    ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(Ox, cy - 22); ctx.lineTo(Ox, cy + 22); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#94a3b8'; ctx.font = '11px sans-serif'; ctx.fillText('O', Ox - 4, cy + 40)
    const dCl = Math.max(-DMAX, Math.min(DMAX, dRef.current))
    const cx = Ox + (dCl / DMAX) * half
    // vật
    const g = ctx.createLinearGradient(cx - 16, cy - 16, cx + 16, cy + 16); g.addColorStop(0, '#60a5fa'); g.addColorStop(1, '#1d4ed8')
    ctx.fillStyle = g; ctx.fillRect(cx - 16, cy - 16, 32, 32)
    // vec tơ vận tốc — gốc trên vật, chiều theo dấu v
    const vv = vRef.current, dir = vv < 0 ? -1 : 1, vl = Math.min(130, Math.abs(vv) * 6)
    if (vl > 2) {
      const ex = cx + dir * vl
      ctx.strokeStyle = '#16a34a'; ctx.fillStyle = '#16a34a'; ctx.lineWidth = 3; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(ex, cy); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(ex, cy); ctx.lineTo(ex - dir * 8, cy - 5); ctx.lineTo(ex - dir * 8, cy + 5); ctx.closePath(); ctx.fill()
      ctx.lineCap = 'butt'
      const mL = cx + (dir * vl) / 2
      ctx.fillStyle = '#16a34a'; ctx.font = 'bold italic 13px sans-serif'; ctx.fillText('v', mL - 3, cy - 8)
      ctx.lineWidth = 1.2; ctx.beginPath(); ctx.moveTo(mL - 5, cy - 20); ctx.lineTo(mL + 5, cy - 20); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(mL + 5, cy - 20); ctx.lineTo(mL + 1, cy - 22); ctx.lineTo(mL + 1, cy - 18); ctx.closePath(); ctx.fill()
    }
    ctx.fillStyle = '#334155'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText(`t = ${tRef.current.toFixed(1)} s · v = ${vv} m/s · d = ${dRef.current.toFixed(1)} m`, x0, 18)
  }, [])

  const drawGraph = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    ctx.clearRect(0, 0, GW, GH)
    const x0 = 56, top0 = 12, bot = GH - 28, midY = (top0 + bot) / 2, plotW = GW - x0 - 16, halfH = (bot - top0) / 2
    const X = (t: number) => x0 + (t / T) * plotW
    const Y = (d: number) => midY - (d / DMAX) * halfH
    // lưới ô (như giấy kẻ ô): phụ mảnh + chính đậm hơn; nhãn ở vạch chính
    ctx.font = '10px sans-serif'
    // dọc: phụ mỗi 0,5 s, chính mỗi 1 s (vạch suốt chiều cao)
    ctx.textAlign = 'center'
    for (let i = 0; i <= T * 2; i++) {
      const t = i / 2, major = i % 2 === 0
      ctx.strokeStyle = major ? '#dbe3ec' : '#f1f5f9'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(X(t), top0); ctx.lineTo(X(t), bot); ctx.stroke()
      if (major && t > 0) { ctx.fillStyle = '#94a3b8'; ctx.fillText(String(t), X(t), bot + 14) }
    }
    // ngang: phụ mỗi 20 m, chính mỗi 40 m — cả phần âm và dương
    ctx.textAlign = 'right'
    for (let d = -DMAX; d <= DMAX; d += 20) {
      if (d === 0) continue
      const major = d % 40 === 0
      ctx.strokeStyle = major ? '#dbe3ec' : '#f1f5f9'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x0, Y(d)); ctx.lineTo(x0 + plotW, Y(d)); ctx.stroke()
      if (major) { ctx.fillStyle = '#94a3b8'; ctx.fillText(String(d), x0 - 6, Y(d) + 3) }
    }
    ctx.textAlign = 'left'
    // trục đậm + mũi tên: d (dọc) tại x0, t (ngang) tại d = 0 (giữa)
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, top0); ctx.lineTo(x0, bot); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, midY); ctx.lineTo(x0 + plotW, midY); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, top0 - 6); ctx.lineTo(x0 - 4, top0 + 2); ctx.lineTo(x0 + 4, top0 + 2); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(x0 + plotW + 6, midY); ctx.lineTo(x0 + plotW - 3, midY - 4); ctx.lineTo(x0 + plotW - 3, midY + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('d (m)', x0 + 4, top0 + 9); ctx.fillText('t (s)', x0 + plotW - 4, midY - 6); ctx.fillStyle = '#64748b'; ctx.fillText('0', x0 - 12, midY + 12)
    // đường d–t đã ghi
    const h = hist.current
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2.8; ctx.beginPath()
    h.forEach((p, i) => { const px = X(p.t), py = Y(p.d); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) })
    ctx.stroke()
    const last = h[h.length - 1]
    if (last) { ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(X(last.t), Y(last.d), 5, 0, Math.PI * 2); ctx.fill() }
  }, [])

  const tick = useCallback(() => {
    if (runRef.current && tRef.current < T) {
      const dt = 0.03
      tRef.current = Math.min(T, tRef.current + dt)
      dRef.current = Math.max(-DMAX, Math.min(DMAX, dRef.current + vRef.current * dt))
      hist.current.push({ t: tRef.current, d: dRef.current })
      if (tRef.current >= T) setRunning(false)
    }
    drawTop(); drawGraph(); raf.current = requestAnimationFrame(tick)
  }, [drawTop, drawGraph])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={top} width={TOPW} height={TOPH} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <canvas ref={gcv} width={GW} height={GH} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <label className="flex items-center gap-2 text-sm mt-3"><span className="w-40 text-gray-600">Vận tốc v (đổi khi đang chạy)</span>
        <input type="range" min={-VMAX} max={VMAX} step={0.5} value={v} onChange={(e) => { touch(); setV(Number(e.target.value)) }} className="flex-1 accent-blue-500" />
        <span className="w-16 text-right font-semibold">{v} m/s</span></label>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); if (tRef.current >= T) reset(); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">{running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { touch(); reset() }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Độ dốc của đồ thị d–t bằng vận tốc. Khi đang chạy, kéo thanh trượt để đổi vận tốc → đoạn đồ thị tiếp theo có độ dốc khác (điểm gãy). v = 0 → đoạn nằm ngang (vật đứng yên); v &lt; 0 → vật đi ngược lại, độ dốc âm (d giảm).</p>
    </div>
  )
}
export default DisplacementTimeSimulation
